/**
 * Market Data — intraday bar subscriptions. Each (symbol, interval) is backfilled on
 * creation and topped up every hour. FX comes from Dukascopy (deep history, free);
 * stocks/ETFs from yfinance (60 days of 2m/5m/15m, 730 days of 60m).
 */

import { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Chip, CircularProgress, Alert, Table, TableBody, TableCell, TableHead, TableRow, TextField, Button, Select, MenuItem, FormControl, InputLabel, IconButton, Tooltip } from '@mui/material';
import { Add, Delete, Storage } from '@mui/icons-material';
import apiService from '../../services/api';

const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '—');

export default function MarketData() {
  const [subs, setSubs] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [symbol, setSymbol] = useState('');
  const [interval, setInterval] = useState('5m');
  const [days, setDays] = useState(180);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = () => apiService.getBarSubscriptions().then(d => setSubs(d.subscriptions)).catch(e => setError(e?.response?.data?.detail || e.message));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!symbol.trim()) return;
    setBusy(true); setMsg(null); setError(null);
    try {
      const r = await apiService.addBarSubscription(symbol.trim(), interval, days);
      setMsg(`${r.symbol} ${r.interval}: ${r.written} bars written${r.error ? ' · error: ' + r.error : ''} · stored ${r.bars} bars ${r.first?.slice(0, 10)} → ${r.last?.slice(0, 16)}`);
      setSymbol(''); await load();
    } catch (e: any) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const remove = async (s: any) => {
    await apiService.removeBarSubscription(s.symbol, s.interval); await load();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Storage sx={{ color: '#3B82F6' }} />
        <Typography variant="h6" fontWeight="bold">Market data — intraday subscriptions</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 980, lineHeight: 1.6 }}>
        A subscription backfills the requested history now and is topped up <b>every hour</b>. FX pairs (EURUSD, GBPJPY, XAUUSD …) come from
        Dukascopy with years of 5-minute history; stocks and ETFs come from yfinance, which offers 60 days of 2/5/15-minute bars and two years of hourly.
        Coarser timeframes (15 min, 1 hour, 6 hours) are built from the finest stored one. Daily and above need no subscription — they come from the daily history.
      </Typography>

      <Card sx={{ mb: 2 }}><CardContent>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" label="Symbol" placeholder="EURUSD, SPY, XAUUSD" value={symbol} onChange={e => setSymbol(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} sx={{ width: 200 }} />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Interval</InputLabel>
            <Select value={interval} label="Interval" onChange={e => setInterval(e.target.value)}>
              {['2m', '5m', '15m', '60m'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="History (days)" type="number" value={days} onChange={e => setDays(Number(e.target.value))} sx={{ width: 140 }} />
          <Button variant="contained" startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Add />} disabled={busy || !symbol.trim()} onClick={add}>Subscribe</Button>
        </Box>
        {msg && <Alert severity="success" sx={{ mt: 1.5 }}>{msg}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      </CardContent></Card>

      <Card><CardContent sx={{ p: 0 }}>
        {!subs ? <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={20} /></Box> : (
          <Table size="small">
            <TableHead><TableRow>
              {['Symbol', 'Interval', 'Source', 'History', 'Bars stored', 'Last bar (UTC)', 'Last ingested', 'Status', ''].map(h => <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: '0.72rem' }}>{h}</TableCell>)}
            </TableRow></TableHead>
            <TableBody>
              {subs.length === 0 && <TableRow><TableCell colSpan={9} sx={{ color: 'text.disabled' }}>No subscriptions.</TableCell></TableRow>}
              {subs.map(s => (
                <TableRow key={`${s.symbol}-${s.interval}`} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{s.symbol}</TableCell>
                  <TableCell>{s.interval}</TableCell>
                  <TableCell><Chip size="small" label={s.source} sx={{ height: 18, fontSize: '0.62rem' }} /></TableCell>
                  <TableCell>{s.lookback_days} d</TableCell>
                  <TableCell>{s.bars?.toLocaleString()}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{s.last_bar_ts ? s.last_bar_ts.slice(0, 16).replace('T', ' ') : '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{fmt(s.last_ingested_at)}</TableCell>
                  <TableCell>{s.last_error ? <Tooltip title={s.last_error}><Chip size="small" color="error" label="error" sx={{ height: 18, fontSize: '0.62rem' }} /></Tooltip> : <Chip size="small" color="success" label="ok" sx={{ height: 18, fontSize: '0.62rem' }} />}</TableCell>
                  <TableCell><IconButton size="small" onClick={() => remove(s)}><Delete sx={{ fontSize: 16 }} /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </Box>
  );
}
